import type { Form } from 'factorial-form'
import deburr from 'lodash/deburr'
import toLower from 'lodash/toLower'
import { computed, makeObservable } from 'mobx'
import type moment from 'moment'

import asyncAction from 'modules/core/lib/asyncAction'
import { Model } from 'modules/core/lib/mobx-rest'

interface ActivateCompanyPageParams {
  slug: string
  country_factorial_domain: string
  public_email: string
}

export interface CompanyDeletion {
  state: string
  access: {
    revoke_at: string
    revoked_at: string
  }
  data: {
    delete_at: string
    deleted_at: string
  }
  backup: {
    url: string
    delete_at: string
    deleted_at: string
  }
  reversible_by_staff: boolean
  reversible_by_owner: boolean
}

interface ICompany {
  [key: string]: any
}

export class Company extends Model<ICompany> {
  static urlRoot() {
    return '/companies'
  }

  constructor(attr: ICompany = {}) {
    super(attr)
    makeObservable(this, {
      isBrokeredByFactorial: computed,
      isMetricable: computed,
      canManagersEditSupplements: computed,
      deletion: computed,
      slug: computed,
      domain: computed,
      publicURL: computed,
      headquarter: computed,
      formNumberOfEmployees: computed,
    })
  }

  get deletion(): CompanyDeletion | null {
    return this.attributes.deletion
  }

  get isBrokeredByFactorial(): boolean {
    return true
  }

  get slug(): string {
    return this.get('slug')
  }

  get domain(): string {
    return this.get('country_factorial_domain')
  }

  get isMetricable(): boolean {
    return this.attributes.metricable
  }

  isRenovationTime(date: moment.Moment): boolean {
    const dueDay = this.attributes.broker_order_due_day

    if (date.month() === 10) {
      return date.date() > dueDay
    } else if (date.month() === 11) {
      return date.date() <= dueDay
    } else {
      return false
    }
  }

  matchesName(search: string): boolean {
    const cleanName = toLower(deburr(this.attributes.name))
    const cleanSearch = toLower(deburr(search))

    return cleanName.includes(cleanSearch)
  }

  get canManagersEditSupplements(): boolean {
    return this.attributes.payroll_supplements_managers_permissions === 'full_visibility'
  }

  get normalizedSize(): string {
    // TODO: (Fran - 08-04-2020) Remove this consitional after some time. We're
    // facing some issues with browser's cache invalidation and when adding new
    // fields into JSON response they don't immediatly catch them.
    //
    // This is here so that we don't break stuff because of telemetry + that
    // caching issue.
    if (this.has('size')) {
      return this.attributes.size
    } else {
      return 'unknown'
    }
  }

  async updateSlugRpc(form: Form, slug: string, domain: string) {
    const update = () =>
      this.rpc('update_slug', {
        slug,
        country_factorial_domain: domain,
      })

    const result = await asyncAction(() => form.handleErrors(update))

    if (result.success) this.set(result.value)

    return Promise.resolve(result)
  }

  async resetAttendanceSecretKeyRpc() {
    return this.rpc('reset_attendance_secret_key', { company_id: this.get('id') }).then(
      (result) => {
        this.set(result)
      }
    )
  }

  async activateCompanyPageRpc(params: ActivateCompanyPageParams) {
    return this.rpc('activate_company_page', params).then((result) => {
      this.set(result)
    })
  }

  get publicURL(): string {
    return this.get('public_url')
  }

  get headquarter(): string {
    return this.attributes.headquarter
  }

  /**
   * Number of employees chosen by the first admin during signup
   */
  get formNumberOfEmployees(): number | undefined {
    return this.attributes.form_number_of_employees
  }
}

export default Company
